import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { message } from 'antd';
import { jwtDecode } from 'jwt-decode';

import { registerAuthHandlers } from '../api/client';
import { loginWithCredentials, DashboardLoginError, logoutSession } from '../api/auth';
import type { DashboardRole, DashboardUser } from '../types/auth';

interface JwtPayload {
  sub?: string;
  email?: string;
  name?: string;
  exp?: number;
  iat?: number;
  jti?: string;
  roles?: string[] | string;
  role?: string[] | string;
  scope?: string;
  [key: string]: unknown;
}

interface AuthContextValue {
  user: DashboardUser | null;
  isAuthenticated: boolean;
  login: (credentials: { email: string; password: string }) => Promise<void>;
  logout: (options?: { redirect?: boolean; reason?: string }) => void;
  ensureAuthenticated: () => void;
  hasRole: (role: DashboardRole) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const STORAGE_KEY = 'sms-dashboard-auth';

const decodeRoles = (payload: JwtPayload, override?: DashboardRole[]): DashboardRole[] => {
  if (override && override.length > 0) {
    return override;
  }

  const rawRoles = payload.roles ?? payload.role ?? payload.scope;
  const normalized: string[] = [];

  if (Array.isArray(rawRoles)) {
    for (const entry of rawRoles) {
      if (typeof entry === 'string' && entry.trim().length > 0) {
        normalized.push(entry.trim().toLowerCase());
      }
    }
  } else if (typeof rawRoles === 'string') {
    for (const part of rawRoles.split(/[\s,]+/)) {
      if (part.trim().length > 0) {
        normalized.push(part.trim().toLowerCase());
      }
    }
  }

  const roles = new Set<DashboardRole>();

  for (const entry of normalized) {
    if (['sms-dashboard-admin', 'admin', 'administrator', 'ops'].includes(entry)) {
      roles.add('admin');
      continue;
    }

    if (['sms-dashboard-readonly', 'readonly', 'read', 'ro', 'viewer'].includes(entry)) {
      roles.add('readonly');
      continue;
    }

    if (['sms-dashboard-audit', 'audit', 'auditor', 'compliance'].includes(entry)) {
      roles.add('audit');
    }
  }

  if (roles.size === 0) {
    roles.add('readonly');
  }

  return [...roles];
};

const normalizeUser = (
  token: string,
  overrides?: Partial<Omit<DashboardUser, 'token'>>
): DashboardUser => {
  const payload = jwtDecode<JwtPayload>(token);

  const userId = overrides?.userId ?? payload.sub?.trim() ?? overrides?.email ?? 'dashboard-user';
  if (!userId || userId.trim().length === 0) {
    throw new Error('Token 未包含用户标识');
  }

  const expiresAtFromToken =
    typeof payload.exp === 'number' ? payload.exp * 1000 : overrides?.expiresAt ?? null;
  if (!expiresAtFromToken) {
    throw new Error('Token 未包含过期时间');
  }

  const roles = decodeRoles(payload, overrides?.roles);
  const email =
    overrides?.email ??
    (typeof payload.email === 'string' && payload.email.trim().length > 0
      ? payload.email.trim()
      : null);
  const name =
    overrides?.name ??
    (typeof payload.name === 'string' && payload.name.trim().length > 0
      ? payload.name.trim()
      : null);
  const issuedAt =
    overrides?.issuedAt ??
    (typeof payload.iat === 'number' ? payload.iat * 1000 : null);

  return {
    token,
    userId,
    email,
    name,
    roles,
    expiresAt: expiresAtFromToken,
    issuedAt
  };
};

const loadStoredUser = (): DashboardUser | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<DashboardUser> & { token?: string };
    if (!parsed.token) return null;

    const user = normalizeUser(parsed.token, {
      userId: parsed.userId,
      email: parsed.email,
      name: parsed.name,
      roles: parsed.roles,
      expiresAt: typeof parsed.expiresAt === 'number' ? parsed.expiresAt : undefined,
      issuedAt: typeof parsed.issuedAt === 'number' ? parsed.issuedAt : undefined
    });

    if (user.expiresAt <= Date.now()) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return user;
  } catch (error) {
    console.warn('读取本地登录状态失败', error);
    window.localStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

const persistUser = (user: DashboardUser | null) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!user) {
    window.localStorage.removeItem(STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
};

const useLoginRedirect = () => {
  return useCallback((returnPath?: string) => {
    if (typeof window === 'undefined') return;

    const currentPath =
      returnPath ??
      `${window.location.pathname}${window.location.search}${window.location.hash}`;

    const sanitized =
      currentPath && currentPath.startsWith('/login') ? '/dashboard' : currentPath;

    const query =
      sanitized && sanitized !== '/login'
        ? `?redirect=${encodeURIComponent(sanitized)}`
        : '';

    window.location.href = `/login${query}`;
  }, []);
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<DashboardUser | null>(() => loadStoredUser());
  const logoutReasonRef = useRef<string | null>(null);
  const redirectToLogin = useLoginRedirect();

  const handleLogout = useCallback(
    ({ redirect = false, reason }: { redirect?: boolean; reason?: string } = {}) => {
      if (user) {
        logoutSession().catch(() => {
          // ignore logout errors
        });
      }

      setUser(null);
      persistUser(null);

      if (reason) {
        logoutReasonRef.current = reason;
      }

      if (redirect) {
        redirectToLogin();
      }
    },
    [user, redirectToLogin]
  );

  const handleUnauthorized = useCallback(() => {
    handleLogout({ redirect: true, reason: '会话失效，请重新登录' });
  }, [handleLogout]);

  useEffect(() => {
    registerAuthHandlers({
      tokenResolver: () => user?.token ?? null,
      onUnauthorized: handleUnauthorized
    });
  }, [user, handleUnauthorized]);

  useEffect(() => {
    if (!user) return;

    persistUser(user);

    const now = Date.now();
    const remaining = user.expiresAt - now;

    if (remaining <= 0) {
      message.warning('登录票据已过期，请重新登录');
      handleLogout({ redirect: true });
      return;
    }

    const warningTimeout = window.setTimeout(() => {
      message.warning('登录票据即将过期，请尽快重新登录');
    }, Math.max(remaining - 60_000, 0));

    const expireTimeout = window.setTimeout(() => {
      handleLogout({ redirect: true, reason: '登录票据已过期，请重新登录' });
    }, remaining);

    return () => {
      window.clearTimeout(warningTimeout);
      window.clearTimeout(expireTimeout);
    };
  }, [user, handleLogout]);

  const login = useCallback(
    async ({ email, password }: { email: string; password: string }) => {
      try {
        const response = await loginWithCredentials(email, password);
        const normalized = normalizeUser(response.token, {
          userId: response.user.userId,
          email: response.user.email,
          name: response.user.name,
          roles: response.user.roles,
          expiresAt: Date.parse(response.expiresAt),
          issuedAt: Date.parse(response.issuedAt)
        });
        setUser(normalized);
        persistUser(normalized);
        message.success('登录成功');
      } catch (error) {
        if (error instanceof DashboardLoginError) {
          message.error(error.message);
          throw error;
        }
        message.error('登录失败，请稍后再试');
        throw error;
      }
    },
    []
  );

  const ensureAuthenticated = useCallback(() => {
    if (user) {
      if (user.expiresAt <= Date.now()) {
        handleLogout({ redirect: true, reason: '登录已过期' });
      }
      return;
    }

    redirectToLogin();
  }, [user, handleLogout, redirectToLogin]);

  const hasRole = useCallback(
    (role: DashboardRole) => {
      if (!user) return false;
      if (user.roles.includes('admin')) return true;
      return user.roles.includes(role);
    },
    [user]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      login,
      logout: handleLogout,
      ensureAuthenticated,
      hasRole
    }),
    [user, login, handleLogout, ensureAuthenticated, hasRole]
  );

  useEffect(() => {
    if (!user && logoutReasonRef.current) {
      message.warning(logoutReasonRef.current);
      logoutReasonRef.current = null;
    }
  }, [user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext 必须在 AuthProvider 内使用');
  }
  return context;
};
